import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Observer } from 'rxjs';
import { BannerInfo, IndexInfo, ExhibitInfo, ExhibitDetail, HistoryExhibitInfo, News, NewsColorType, NewsDetail, Law, Book, LinkInfo, Multimedia, Faq, Feedback, ExhibitDetailImg } from './http-model';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  constructor(private http: HttpClient) { }

  domain = environment.domain;
  logDomain = environment.logDomain;
  imgDomain = environment.imgDomain;

  getIndexInfo(language: string): Observable<IndexInfo> {
    const url = `${this.domain}/Index/${language}`;

    return this.http.get<IndexInfo>(url).pipe(
      map(d => {
        d.banner.forEach(b => {
          b.imgSrc = `${this.imgDomain}/ExhibitsImg/web/${b.imgSrc}`;
        });
        d.message.forEach(b => {
          b.fileName = `${this.imgDomain}/ExhibitsImg/web/${b.fileName}`;
        });
        d.promotion.forEach(b => {
          b.fileName = `${this.imgDomain}/ExhibitsImg/web/${b.fileName}`;
        });
        d.sku.forEach(b => {
          b.ThumbnailImg = `${this.imgDomain}/ExhibitsImg/Thumbnail/${b.ThumbnailImg}`;
        });

        return d;
      })
    );
  }

  getCirculiation(language: string, typeId: number): Observable<ExhibitInfo[]> {
    let urlPath = 'circulation';
    if (language === 'en-us') {
      urlPath = 'circulation_en';
    }

    const url = `${this.domain}/${urlPath}/${typeId}`;

    if (language === 'en-us') {
      return this.http.get<any>(url)
        .pipe(
          map(d => {
            let result: ExhibitInfo[] = [];
            d.forEach(element => {
              result.push(
                {
                  ExhibitsId: element.en_ExhibitsId,
                  ExhibitsName: element.en_ExhibitsName,
                  ThumbnailImg: `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.exh_ThumbnailImg}`
                }
              );
            });
            return result;
          })
        );
    } else {
      return this.http.get<ExhibitInfo[]>(url)
        .pipe(
          map(d => {
            d.forEach(element => {
              element.ThumbnailImg = `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.ThumbnailImg}`;
            });
            return d;
          })
        );
    }
  }

  getExhibitDetail(language: string, id: number): Observable<ExhibitDetail> {
    let urlPath = 'exhibits';
    if (language === 'en-us') {
      urlPath = 'exhibits_en';
    }

    const url = `${this.domain}/${urlPath}/${id}`;

    if (language === 'en-us') {
      return this.http.get<any>(url)
        .pipe(
          map(d => {
            let imgs: ExhibitDetailImg[] = [];
            d.img.forEach(element => {
              imgs.push({
                imgType: element.imgType,
                imgSrc: `${this.imgDomain}/ExhibitsImg/Search/${element.imgSrc}`,
                imgName: element.imgName
              });
            });

            let result: ExhibitDetail = {
              ExhibitsId: d.ExhibitsId,
              ExhibitsName: d.en[0].ExhibitsName,
              Obj_Type: d.Obj_Type,
              SurroundSwf: d.SurroundSwf,
              ExtContent: d.ExtContent,
              ExhibitsType: d.ExhibitsType,
              IssueTime: d.en[0].IssueTime,
              PlateYear: d.en[0].PlateYear,
              size_L: d.en[0].size_L,
              size_W: d.en[0].size_W,
              size_H: d.en[0].size_H,
              Obj_color1: d.en[0].Obj_color1,
              Obj_color2: d.en[0].Obj_color2,
              Weight: d.en[0].Weight,
              Diameter: d.en[0].Diameter,
              Dollar: d.en[0].Dollar,
              Area: d.en[0].Area,
              obj_material: d.en[0].obj_material,
              ExhibitsVers: d.en[0].ExhibitsVers,
              WebImg: `${this.imgDomain}/ExhibitsImg/WebImg/${d.en[0].WebImg}`,
              imgDescr1: d.en[0].imgDescr1,
              imgDescr2: d.en[0].imgDescr2,
              imgDescr3: d.en[0].imgDescr3,
              Exhibits_Content: d.en[0].Exhibits_Content,
              img: imgs,
              ebook: d.ebook,
              FakeWeb: d.en[0].FakeWeb
            };

            return result;
          })
        );
    } else {
      return this.http.get<ExhibitDetail>(url)
        .pipe(
          map(d => {
            d.WebImg = `${this.imgDomain}/ExhibitsImg/WebImg/${d.WebImg}`;
            d.img.forEach(chi => {
              chi.imgSrc = `${this.imgDomain}/ExhibitsImg/Search/${chi.imgSrc}`;
            });

            return d;
          })
        );
    }
  }

  getCommemorate(language: string, typeId: number): Observable<ExhibitInfo[]> {
    let urlPath = 'commemorate';
    if (language === 'en-us') {
      urlPath = 'commemorate_en';
    }

    const url = `${this.domain}/${urlPath}/${typeId}`;

    if (language === 'en-us') {
      return this.http.get<any>(url)
        .pipe(
          map(d => {
            let result: ExhibitInfo[] = [];
            d.forEach(element => {
              result.push(
                {
                  ExhibitsId: element.en_ExhibitsId,
                  ExhibitsName: element.en_ExhibitsName,
                  ThumbnailImg: `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.exh_ThumbnailImg}`
                }
              );
            });
            return result;
          })
        );
    } else {
      return this.http.get<ExhibitInfo[]>(url)
        .pipe(
          map(d => {
            d.forEach(element => {
              element.ThumbnailImg = `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.ThumbnailImg}`;
            });
            return d;
          })
        );
    }
  }

  getHistory(language: string, typeId: number): Observable<HistoryExhibitInfo[]> {
    let urlPath = 'story';
    if (language === 'en-us') {
      urlPath = 'story_en';
    }

    const url = `${this.domain}/${urlPath}/${typeId}`;

    if (language === 'en-us') {
      return this.http.get<any>(url)
        .pipe(
          map(d => {
            let result: HistoryExhibitInfo[] = [];
            d.forEach(element => {
              result.push(
                {
                  ExhibitsId: element.en_ExhibitsId,
                  ExhibitsName: element.en_ExhibitsName,
                  ThumbnailImg: `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.exh_ThumbnailImg}`,
                  WebImg: `${this.imgDomain}/ExhibitsImg/WebImg/${element.en_WebImg}`,
                  PlateYear: element.en_PlateYear
                }
              );
            });
            return result;
          })
        );
    } else {
      return this.http.get<HistoryExhibitInfo[]>(url)
        .pipe(
          map(d => {
            d.forEach(element => {
              element.ThumbnailImg = `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.ThumbnailImg}`;
              element.WebImg = `${this.imgDomain}/ExhibitsImg/WebImg/${element.WebImg}`;
            });
            return d;
          })
        );
    }
  }

  getNews(language: string): Observable<News[]> {
    const url = `${this.domain}/news/${language}`;

    return this.http.get<News[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            switch (element.Color) {
              case NewsColorType.public:
                element.ColorName = '國內券幣';
                break;
              case NewsColorType.propaganda:
                element.ColorName = '券幣大小事';
                break;
              case NewsColorType.education:
                element.ColorName = '防偽辨識';
                break;
              case NewsColorType.activity:
                element.ColorName = '紀念性券幣銷售';
                break;
              case NewsColorType.downtime:
                element.ColorName = '國外券幣';
                break;
              case NewsColorType.other:
                element.ColorName = '其他';
                break;
              case NewsColorType.otherinformation:
                element.ColorName = '其他資訊';
                break;
              case NewsColorType.financial:
                element.ColorName = '金融文物館';
                break;
              default:
                break;
            }
            element.fileName = `${this.imgDomain}/ExhibitsImg/web/${element.fileName}`;
          });
          return d;
        })
      );
  }

  getNewsDetail(id: number): Observable<NewsDetail> {
    const url = `${this.domain}/newspage/${id}`;

    return this.http.get<NewsDetail>(url)
      .pipe(
        map(element => {
          switch (element.Color) {
            case NewsColorType.public:
              element.ColorName = '國內券幣';
              break;
            case NewsColorType.propaganda:
              element.ColorName = '券幣大小事';
              break;
            case NewsColorType.education:
              element.ColorName = '防偽辨識';
              break;
            case NewsColorType.activity:
              element.ColorName = '紀念性券幣銷售';
              break;
            case NewsColorType.downtime:
              element.ColorName = '國外券幣';
              break;
            case NewsColorType.other:
              element.ColorName = '其他';
              break;
            case NewsColorType.otherinformation:
              element.ColorName = '其他資訊';
              break;
            case NewsColorType.financial:
              element.ColorName = '金融文物館';
              break;
            default:
              break;
          }
          if (element.fileName) {
          element.fileName = `${this.imgDomain}/ExhibitsImg/web/${element.fileName}`;}
          if (element.fileUrl) {
            element.fileUrl = `${this.imgDomain}/ExhibitsImg/web/${element.fileUrl}`;
          }

          return element;
        })
      );
  }

  getLaws(language: string): Observable<Law[]> {
    let urlPath = '';
    if (language === 'en-us') {
      urlPath = 'en';
    }
    const url = `${this.domain}/law/${urlPath}`;

    return this.http.get<Law[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            // element.ThumbnailImg = `${this.imgDomain}/ExhibitsImg/Thumbnail/${element.ThumbnailImg}`;
            // element.WebImg = `${this.imgDomain}/ExhibitsImg/WebImg/${element.WebImg}`;
          });
          return d;
        })
      );
  }

  getBooks(): Observable<Book[]> {
    const url = `${this.domain}/booklist`;

    return this.http.get<Book[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            element.File1 = `${this.imgDomain}/ExhibitsImg/web/${element.File1}`;
            element.File2 = `${this.imgDomain}/ExhibitsImg/PDF/${element.File2}`;
            element.File3 = `${this.imgDomain}/ExhibitsImg/publication/1/${element.File3}`;
          });
          return d;
        })
      );
  }

  getLinkInfos(): Observable<LinkInfo[]> {
    const url = `${this.domain}/link`;

    return this.http.get<LinkInfo[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            element.ImgSrc = `${this.imgDomain}/ExhibitsImg/web/${element.ImgSrc}`;
          });
          return d;
        })
      );
  }

  getMultimedias(typeId: number): Observable<Multimedia[]> {
    const url = `${this.domain}/datadownload/${typeId}`;

    return this.http.get<Multimedia[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            element.File1 = `${this.imgDomain}/ExhibitsImg/web/${element.File1}`;
            element.File2 = `${this.imgDomain}/ExhibitsImg/web/${element.File2}`;
            element.File3 = `${this.imgDomain}/ExhibitsImg/web/${element.File3}`;
            element.File4 = `${this.imgDomain}/ExhibitsImg/web/${element.File4}`;
            element.File5 = `${this.imgDomain}/ExhibitsImg/web/${element.File5}`;
          });
          return d;
        })
      );
  }

  getMultimedias_Gmae(typeId: number): Observable<Multimedia[]> {
    const url = `${this.domain}/datadownload/${typeId}`;

    return this.http.get<Multimedia[]>(url)
      .pipe(
        map(d => {
          d.forEach(element => {
            element.File1 = `${this.imgDomain}/ExhibitsImg/web/${element.File1}`;
            element.File2 = `${this.imgDomain}/ExhibitsImg/Game/${element.ID}/${element.File2}`;
          });
          return d;
        })
      );
  }

  getFaqs(language: string): Observable<Faq[]> {
    const url = `${this.domain}/faq/${language}`;

    return this.http.get<Faq[]>(url);
  }

  sendFeedback(name: string, email: string, title: string, content: string, phone: string): Observable<void> {
    const url = `${this.domain}/feedback`;
    const body = {
      SendName: name,
      email: email,
      Sendtitle: title,
      content: content,
      tel: phone
    };

    return this.http.post<void>(url, body);
  }

  getFeedbacks(email: string): Observable<Feedback[]> {
    const url = `${this.domain}/feedback/${email}`;

    return this.http.get<Feedback[]>(url);
  }

  exhibitStastics(id: number, type: string): Observable<void> {
    const url = `${this.logDomain}/exh`;
    const body = {
      type: type,
      ExhibitsID: id
    };

    return this.http.post<void>(url, body);
  }

  pageStastics(type: string, subType: string, pageUrl: string): Observable<void> {
    const url = `${this.logDomain}/stastics`;
    const body = {
      Type: type,
      subType: subType,
      links: pageUrl
    };

    return this.http.post<void>(url, body);
  }

  pageStasticsForMultimedia(type: string, subType: string, pageUrl: string, typeId: number): Observable<void> {
    const url = `${this.logDomain}/stastics`;
    const body = {
      Type: type,
      subType: subType,
      links: pageUrl,
      url: typeId
    };

    return this.http.post<void>(url, body);
  }
}
